import React, { Component } from "react";
import { MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import profile_default from "images/profile_default.png";
import DragSortableList from "../../../../../CC_cubi_clan_project/GlobalComponents/DragSortableList/DragSortableList";
import { toDataUrl } from "../../../../../CC_cubi_clan_project/GlobalComponents/DragSortableList/dndUtils";
import { mfmFramedUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import "./CastSection.css";

const PORTRAIT_FORMAT_KEY = "ratio:3:4";
const PORTRAIT_W = 270;
const PORTRAIT_H = 360;

/**
 * Props:
 *  - personajes: array (datos originales, estructura personajes → roles)
 *  - onEdit(index, item)        // índice del personaje en el array del padre
 *  - onCreate()
 *  - onChange(updatedPersonajes) // devuelve personajes con roles actualizados
 *
 * Internamente aplana todos los roles de todos los personajes para mostrar
 * una tarjeta por rol. El drag-and-drop reordena a nivel de rol.
 */

export default class CastSection extends Component {
  static defaultProps = { personajes: [] };

  /** Aplana personajes → roles, enriquecido con refs al padre.
   *  Si un personaje no tiene roles, se emite una entrada con rol vacío. */
  flattenRoles = () => {
    const flat = [];
    (this.props.personajes || []).forEach((per, perIdx) => {
      if (per.roles && per.roles.length > 0) {
        per.roles.forEach((rol, rolIdx) => {
          flat.push({
            ...rol,
            status: per.status || "published",
            _personaje: per,
            _perIdx: perIdx,
            _rolIdx: rolIdx,
            _hasRole: true,
          });
        });
      } else {
        // Personaje sin roles: entrada virtual para que aparezca en la lista
        flat.push({
          sortOrder: per.sortOrder ?? 0,
          status: per.status || "published",
          _personaje: per,
          _perIdx: perIdx,
          _rolIdx: -1,
          _hasRole: false,
        });
      }
    });
    return flat;
  };

  /** Tras reordenar en DnD: actualizar sortOrder en los roles originales */
  handleReorder = (updatedFlat) => {
    const personajes = JSON.parse(JSON.stringify(this.props.personajes || []));
    updatedFlat.forEach((fr) => {
      const per = personajes[fr._perIdx];
      if (fr._hasRole && per?.roles?.[fr._rolIdx]) {
        per.roles[fr._rolIdx].sortOrder = fr.sortOrder;
      } else if (!fr._hasRole && per) {
        per.sortOrder = fr.sortOrder;
      }
    });
    this.props.onChange && this.props.onChange(personajes);
  };

  /** Toggle status del personaje: draft ↔ published (sin abrir modal) */
  handleStatusToggle = (perIdx, rolIdx, e) => {
    e.stopPropagation();
    const personajes = JSON.parse(JSON.stringify(this.props.personajes || []));
    const personaje = personajes[perIdx];
    if (personaje) {
      personaje.status = personaje.status === "published" ? "draft" : "published";
    }
    this.props.onChange && this.props.onChange(personajes);
  };

  /** Click en tarjeta → abrir modal del personaje/rol correspondiente */
  handleEditClick = (visibleIndex, flatRole) => {
    const perIdx = flatRole._perIdx;
    const personaje = this.props.personajes[perIdx];
    if (!personaje) return;

    const rolIdx = flatRole._hasRole ? flatRole._rolIdx : -1;
    const selectedRole =
      rolIdx >= 0 && Array.isArray(personaje.roles)
        ? personaje.roles[rolIdx]
        : null;

    const payload = selectedRole
      ? { ...personaje, roles: [selectedRole] }
      : personaje;

    this.props.onEdit && this.props.onEdit(perIdx, payload, rolIdx);
  };

  /* ---------- Tarjeta por ROL (formato carnet) ---------- */
  renderItem = (flatRole, idx, { over }) => {
    const personaje = flatRole._personaje;
    const actor = flatRole._hasRole ? (flatRole.actor || {}) : {};
    const roleImg = flatRole._hasRole
      ? (flatRole.imageUuid ? mfmFramedUrl(flatRole.imageUuid, PORTRAIT_W, PORTRAIT_H, PORTRAIT_FORMAT_KEY) : toDataUrl(flatRole.imageBase64))
      : null;
    const actorIsPublished = actor?.status === "published";
    const actorImg = actorIsPublished
      ? (actor?.imageUuid ? mfmFramedUrl(actor.imageUuid, PORTRAIT_W, PORTRAIT_H, PORTRAIT_FORMAT_KEY) : toDataUrl(actor?.imageBase64))
      : null;
    const personajeImg = personaje?.imageUuid
      ? mfmFramedUrl(personaje.imageUuid, PORTRAIT_W, PORTRAIT_H, PORTRAIT_FORMAT_KEY)
      : toDataUrl(personaje?.imageBase64);
    const cardImg = roleImg || personajeImg || actorImg || profile_default;
    const title = personaje?.name || "";
    const subtitle = actor?.name || "";
    const isDraft = flatRole.status === "draft";
    const badgeText = isDraft ? "Borrador" : "Publicado";
    const badgeTitleHint = isDraft ? "Borrador – clic para publicar" : "Publicado – clic para pasar a borrador";

    return (
      <div className="cast-card-wrapper" onClick={() => this.handleEditClick(idx, flatRole)}>
        <MDBCard
          className={`cast-card clickable ${over ? "is-over" : ""} ${isDraft ? "cast-card-draft" : ""}`}
          title={`Orden ${flatRole.sortOrder}`}
        >
          <div className="cast-avatar">
            <img className="cast-img" src={cardImg} alt={title || "reparto"} draggable={false} />
            <span
              className={`cast-status-badge ${isDraft ? "badge-draft" : "badge-published"}`}
              onClick={(e) => this.handleStatusToggle(flatRole._perIdx, flatRole._rolIdx, e)}
              title={badgeTitleHint}
            >
              {badgeText}
            </span>
          </div>

          <MDBCardBody className="cast-info">
            {title ? <div className="cast-card-title">{title}</div> : null}
            {subtitle ? <div className="cast-card-subtitle">{subtitle}</div> : null}
          </MDBCardBody>
        </MDBCard>
      </div>
    );
  };

  render() {
    const { onCreate } = this.props;
    const flatRoles = this.flattenRoles();

    return (
      <section className="cast-section">
        <h3 className="project-subtitle">Reparto</h3>

        <DragSortableList
          items={flatRoles}
          orderProp="sortOrder"
          getKey={(item, idx) => item._hasRole ? `role-${item.idRol || idx}` : `per-${item._personaje?.idPersonaje || idx}`}
          renderItem={this.renderItem}
          onChange={this.handleReorder}
        />

        {/* Botón "Añadir" */}
        <div className="comments-add-tile" onClick={() => onCreate && onCreate()}>
          <span className="comments-add-plus">＋</span>
          <span>Añadir personaje</span>
        </div>
      </section>
    );
  }
}
